import { defaultWebsiteConfig } from "./websiteConfig";

/**
 * Designer-local zone shape. Mirrors the wire shape except for the
 * client-only `_uid` used to track newly-added (not-yet-saved) rows,
 * and `id` is optional for the same reason.
 */
export type WidgetType =
  | "image"
  | "video"
  | "website"
  | "iframe_map"
  | "iframe_embed"
  | "text"
  | "clock"
  | "weather"
  | "qr"
  | "menu_board";

// Palette order. Map + Embed are first-class content types (previously hidden
// inside the Website "display mode" dropdown). They reuse the website-family
// widget_types the player/backend already recognise, so they render across the
// editor, preview, web player and device with no schema change.
export const WIDGET_TYPES: WidgetType[] = [
  "image", "video", "website", "iframe_map", "iframe_embed", "text", "clock", "weather", "qr", "menu_board",
];

export const WIDGET_LABELS: Record<WidgetType, string> = {
  image: "Image",
  video: "Video",
  website: "Website / URL",
  iframe_map: "Map",
  iframe_embed: "Embed / iFrame",
  text: "Text",
  clock: "Clock",
  weather: "Weather",
  qr: "QR Code",
  menu_board: "Menu Board",
};

/**
 * Website-family widget types — a website/iframe zone whose body is rendered by
 * WebZoneView from a sanitized `url` + `display_mode`. `iframe_video` is not a
 * palette widget but legacy/imported payloads can carry it, so it's recognised
 * here for canvas behaviour (drag overlay, live preview).
 */
export const isWebsiteFamilyType = (t: string): boolean =>
  t === "website" || t === "iframe_map" || t === "iframe_embed" || t === "iframe_video";

/**
 * The widget "kind" the editor should present for a zone — drives which
 * inspector panel + canvas label to show and the Widget selector's value.
 *
 * Backward compatibility: older templates stored every website-family zone as
 * widget_type `website` with the content type in `widget_config.display_mode`.
 * We derive the kind from BOTH so those zones open in the right inspector
 * WITHOUT rewriting the stored record (it re-saves under its original type and
 * keeps rendering identically). New Map/Embed zones carry the concrete
 * widget_type directly. Legacy embedded/direct video maps to the Embed
 * inspector, where it stays editable and renders exactly as saved.
 */
export const editorWidgetKind = (
  zone: { widget_type: string; widget_config: Record<string, unknown> },
): WidgetType => {
  const wt = zone.widget_type;
  if (wt === "iframe_map") return "iframe_map";
  if (wt === "iframe_embed") return "iframe_embed";
  if (wt === "website" || wt === "iframe_video") {
    const dm = (zone.widget_config as Record<string, unknown>)?.display_mode;
    if (dm === "iframe_map") return "iframe_map";
    if (dm === "iframe_embed" || dm === "iframe_video" || dm === "direct_video") return "iframe_embed";
    return "website";
  }
  return (WIDGET_TYPES as string[]).includes(wt) ? (wt as WidgetType) : "website";
};

export interface DesignerZone {
  id?: number;
  _uid: string; // client-side stable id for React keys + DnD
  name: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
  z_order: number;
  widget_type: WidgetType;
  widget_config: Record<string, unknown>;
}

export interface DesignerTemplate {
  id: number;
  name: string;
  orientation: "landscape" | "portrait";
  canvas_width: number;
  canvas_height: number;
  background: string;
  version: number;
  is_active: boolean;
  zones: DesignerZone[];
}

let uidCounter = 1;
export const nextUid = (): string => `z-${Date.now().toString(36)}-${uidCounter++}`;

export const defaultConfigFor = (type: WidgetType): Record<string, unknown> => {
  switch (type) {
    case "image":      return { media_id: null, fit: "cover" };
    case "video":      return { media_id: null, source: "library", video_url: "", loop: true, muted: true, fit: "cover" };
    case "website":    return { ...defaultWebsiteConfig(), display_mode: "website" } as unknown as Record<string, unknown>;
    case "iframe_map": return { ...defaultWebsiteConfig(), display_mode: "iframe_map", fit: "fill", allow_interaction: false } as unknown as Record<string, unknown>;
    case "iframe_embed": return { ...defaultWebsiteConfig(), display_mode: "iframe_embed" } as unknown as Record<string, unknown>;
    case "text":       return { content: "Sample text", font_size: 48, weight: 700, color: "#FFFFFF", align: "center" };
    case "clock":      return { format: "HH:mm", timezone: "", font_scale: 100 };
    case "weather":    return { latitude: null, longitude: null, location_name: "", timezone: null, units: "metric", font_scale: 100, color: "#FFFFFF" };
    case "qr":         return { content: "https://example.com", fg: "#000000", bg: "#FFFFFF" };
    case "menu_board": return { source: "products", columns: 2 };
  }
};
